import {
  StorageMediaData,
  StorageProviderBase,
  StorageProviderTypeEnum,
  StorageStoreResults,
} from '@rendley/sdk';

import { config } from '@/shared/lib';

const API_BASE = `https://${config.API_ENDPOINT}/content-storage/storage`;

export class StorageS3WithPresignedURLs extends StorageProviderBase {
  projectId!: string;

  constructor() {
    super(StorageProviderTypeEnum.REMOTE);
  }

  // content-storage needs the publisher session; presigned S3 URLs must not.
  static authHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');

    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  static castToHTTPS(url: string): string {
    return url.replace(/^http:/, 'https:');
  }

  async init(projectId: string): Promise<void> {
    this.projectId = projectId;
  }

  async destroy(): Promise<void> {
    // eslint-disable-next-line no-useless-return
    return;
  }

  async storeMedia(
    storageData: StorageMediaData
  ): Promise<StorageStoreResults> {
    const result: StorageStoreResults = { processNext: true, success: false };

    const uploadPresignedUrl = await this.getUploadPresignedUrl(
      storageData.hash
    );
    const data = await this.uploadToPresignedUrl(
      uploadPresignedUrl,
      storageData.data
    );

    if (data == null) {
      throw new Error('The file could not be uploaded.');
    }

    result.success = true;

    return result;
  }

  async hasMedia(mediaHash: string): Promise<boolean> {
    try {
      const hashList = await this.getMediaHashList();
      return hashList.includes(mediaHash);
    } catch {
      return false;
    }
  }

  async getMedia(mediaHash: string): Promise<StorageMediaData | null> {
    const retrievePresignedUrl = await this.getRetrievePresignedUrl(mediaHash);
    const response = await fetch(retrievePresignedUrl);

    if (!response.ok) {
      throw new Error('Failed to download media from storage.');
    }

    const data = await response.arrayBuffer();

    // @ts-ignore
    return {
      hash: mediaHash,
      data: new Uint8Array(data),
    };
  }

  async removeMedia(mediaHash: string): Promise<boolean> {
    try {
      const response = await fetch(this.getDeleteEndpoint(mediaHash), {
        method: 'DELETE',
        headers: StorageS3WithPresignedURLs.authHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to remove media from storage');
      }

      const result = await response.json();

      return result.data;
    } catch (error) {
      console.error({ error });
      return false;
    }
  }

  async getMediaHashList(): Promise<string[]> {
    try {
      const response = await fetch(this.getListHashListEndpoint(), {
        headers: StorageS3WithPresignedURLs.authHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to get media list from storage');
      }

      const result = await response.json();

      return result.data;
    } catch (error) {
      console.error({ error });
      return [];
    }
  }

  async sync(master: StorageProviderBase): Promise<boolean> {
    if (master instanceof StorageS3WithPresignedURLs) {
      return Promise.resolve(false);
    }

    let wasChanged = false;

    const masterHashList = await master.getMediaHashList();
    const ourHashList = await this.getMediaHashList();

    const removeList = ourHashList.filter(
      (hash) => !masterHashList.includes(hash)
    );

    // eslint-disable-next-line no-restricted-syntax
    for (const hash of removeList) {
      await this.removeMedia(hash);
      wasChanged = true;
    }

    const addList = masterHashList.filter(
      (hash) => !ourHashList.includes(hash)
    );

    // eslint-disable-next-line no-restricted-syntax
    for (const hash of addList) {
      const storageData = await master.getMedia(hash);
      if (storageData) {
        await this.storeMedia(storageData);
        wasChanged = true;
      } else {
        return Promise.reject(new Error(`Unable to get media ${hash}`));
      }
    }

    return Promise.resolve(wasChanged);
  }

  isActive(): boolean {
    return true;
  }

  private getUploadEndpoint(hash: string) {
    return `${API_BASE}/${this.projectId}/${hash}/upload`;
  }

  private getRetrieveEndpoint(hash: string) {
    return `${API_BASE}/${this.projectId}/${hash}/retrieve`;
  }

  private getDeleteEndpoint(hash: string) {
    return `${API_BASE}/${this.projectId}/${hash}`;
  }

  private getListHashListEndpoint() {
    return `${API_BASE}/${this.projectId}`;
  }

  private async getUploadPresignedUrl(hash: string): Promise<string> {
    const response = await fetch(this.getUploadEndpoint(hash), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...StorageS3WithPresignedURLs.authHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get signed URL from Lambda');
    }

    const data = await response.json();

    return StorageS3WithPresignedURLs.castToHTTPS(data.data as string);
  }

  private async getRetrievePresignedUrl(hash: string): Promise<string> {
    const response = await fetch(this.getRetrieveEndpoint(hash), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...StorageS3WithPresignedURLs.authHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get signed URL from Lambda');
    }

    const data = await response.json();

    return StorageS3WithPresignedURLs.castToHTTPS(data.data as string);
  }

  private async uploadToPresignedUrl(
    presignedUrl: string,
    data: Uint8Array
  ): Promise<string> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      // @ts-ignore
      body: data,
    });

    if (!response.ok) {
      throw new Error('Failed to upload file to S3');
    }

    return presignedUrl;
  }
}
